import { isValidToolName, parseActions } from './parseActions';
import { CONTINUE_NUDGE, pruneMessagesToBudget } from './pruneMessages';
import { stripFences } from './stripFences';
import { mapNativeToolCalls } from './toolCalls';
import { formatToolResults, hasNativeToolTurns, nativeToXmlMessages } from './xmlHistory';
import type {
  ActionResult,
  AgentHost,
  AgentMessage,
  AgentToolMode,
  NativeToolCall,
  ParsedAction,
  ParseResult,
  RunLoopOptions,
  RunLoopResult,
} from './types';

export const DEFAULT_MAX_TURNS = 32;
export const DEFAULT_MAX_ACTIONS_PER_TURN = 12;
export const AGENT_MAX_OUTPUT_TOKENS = 16384;

export function isToolsUnsupportedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const typed = error as { type?: unknown; name?: unknown };
  return typed.type === 'tools_unsupported' || typed.name === 'ToolsUnsupportedError';
}

export function isLengthFinish(reason: string | null | undefined): boolean {
  if (!reason) return false;
  const normalized = reason.toLowerCase();
  return normalized === 'length' || normalized === 'max_tokens' || normalized === 'max_output_tokens';
}

function unfinishedNameFromRaw(raw: string): string | null {
  const named = /\bname\s*=\s*"([^"]+)"/i.exec(raw);
  if (named?.[1] && named[1].toLowerCase() !== 'tool_name') return named[1];
  const fence = /^<<<([A-Za-z][A-Za-z0-9_]*)/.exec(raw.trim());
  if (fence?.[1]) return fence[1];
  const afterOpen = raw.replace(/^[\s\S]*?<tool_call[^>]*>/i, '');
  const first = afterOpen.trim().split('\n')[0]?.trim().replace(/\(\s*\)\s*$/, '');
  if (first && /^[A-Za-z][A-Za-z0-9_]*$/.test(first) && first.toLowerCase() !== 'tool_name') {
    return first;
  }
  return null;
}

function appliedLabels(results: ActionResult[]): string[] {
  const labels: string[] = [];
  for (const result of results) {
    if (!result.ok) continue;
    const ok = /^ok\s+(.+)$/.exec(result.message.trim());
    labels.push(ok?.[1] ?? result.message.trim() ?? result.toolName);
  }
  return labels;
}

export function formatIncompleteAction(
  parsed: ParseResult,
  applied: ActionResult[],
  unfinished?: string | null,
): ActionResult {
  const incomplete = parsed.segments.find((segment) => segment.kind === 'incomplete');
  const raw = incomplete && incomplete.kind === 'incomplete' ? incomplete.raw : '';
  const name = unfinished ?? unfinishedNameFromRaw(raw);
  const stub = raw.replace(/\s+/g, ' ').trim().slice(0, 240);
  const appliedText = appliedLabels(applied);
  const parts = ['incomplete_action: a tool_call was cut off before it was closed.'];
  if (appliedText.length > 0) parts.push(`Applied: ${appliedText.join(', ')}.`);
  if (name) parts.push(`Unfinished: ${name}.`);
  if (stub) parts.push(`Stub: ${stub}`);
  parts.push('Re-emit ONLY the unfinished call, fully closed. Do not repeat applied calls.');
  return {
    ok: false,
    toolName: 'incomplete_action',
    message: parts.join(' '),
  };
}

function formatIncompleteNative(call: NativeToolCall, applied: ActionResult[]): ActionResult {
  const stub = call.arguments.replace(/\s+/g, ' ').trim().slice(0, 240);
  const appliedText = appliedLabels(applied);
  const parts = ['incomplete_action: a tool call JSON was cut off.'];
  if (appliedText.length > 0) parts.push(`Applied: ${appliedText.join(', ')}.`);
  if (call.name) parts.push(`Unfinished: ${call.name}.`);
  if (stub) parts.push(`Stub: ${stub}`);
  parts.push('Re-emit ONLY the unfinished call as valid JSON. Do not repeat applied calls.');
  return {
    ok: false,
    toolName: 'incomplete_action',
    message: parts.join(' '),
  };
}

function formatInvalidNative(call: NativeToolCall): ActionResult {
  const name = call.name.trim();
  const problem = isValidToolName(name) ? 'its arguments are not valid JSON' : 'its name is not a tool name';
  const stub = call.arguments.replace(/\s+/g, ' ').trim().slice(0, 240);
  const parts = [`error: this call could not be read: ${problem}.`];
  if (stub) parts.push(`Stub: ${stub}`);
  parts.push('Re-send only this call, fixed.');
  return {
    ok: false,
    toolName: name || 'unknown_action',
    message: parts.join(' '),
  };
}

// A throw would skip flush() and lose the run's earlier writes.
async function executeAction(host: AgentHost, action: ParsedAction): Promise<ActionResult> {
  try {
    return await host.execute(action);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return { ok: false, toolName: action.name, message: `error: ${message}` };
  }
}

export async function runLoop(options: RunLoopOptions): Promise<RunLoopResult> {
  const {
    host,
    complete,
    userMessage,
    history = [],
    onEvent,
    onChunk,
    onPrompt,
    isAborted = () => false,
    maxTurns = DEFAULT_MAX_TURNS,
    maxActionsPerTurn = DEFAULT_MAX_ACTIONS_PER_TURN,
    toolMode: initialToolMode,
    maxInputTokens,
    measurePrompt,
  } = options;

  const emit = onEvent ?? (() => undefined);

  const budgeted = (prompt: AgentMessage[]): AgentMessage[] => {
    if (maxInputTokens == null || !measurePrompt) return prompt;
    return pruneMessagesToBudget(prompt, maxInputTokens, measurePrompt);
  };

  let toolMode: AgentToolMode =
    initialToolMode ?? (host.tools && host.tools.length > 0 ? 'native' : 'xml');
  const extra = await host.extraContextChunks();
  const priorTurns = history.filter((message) => message.role !== 'system');
  const messages: AgentMessage[] = [
    { role: 'system', content: host.buildSystemPrompt({ extraChunks: extra, toolMode }) },
    ...(toolMode === 'xml' && hasNativeToolTurns(priorTurns)
      ? nativeToXmlMessages(priorTurns)
      : priorTurns),
    { role: 'user', content: userMessage },
  ];
  const notifyPrompt = (prompt: AgentMessage[] = messages) => {
    onPrompt?.(prompt);
  };

  const finish = async (reason: RunLoopResult['reason']): Promise<RunLoopResult> => {
    await host.flush?.();
    emit({ type: 'done', reason });
    return { reason };
  };

  for (let turn = 0; turn < maxTurns; turn += 1) {
    if (isAborted()) {
      return finish('abort');
    }

    let content: string;
    let reasoning: string | undefined;
    let finishReason: string | null | undefined;
    let toolCalls: NativeToolCall[] = [];
    try {
      const requestMessages = budgeted(messages);
      notifyPrompt(requestMessages);
      const result = await complete(requestMessages, onChunk);
      content = result.content ?? '';
      reasoning = result.reasoning;
      finishReason = result.finishReason;
      toolCalls = result.toolCalls ?? [];
    } catch (err) {
      if (isAborted()) {
        return finish('abort');
      }
      if (isToolsUnsupportedError(err) && toolMode === 'native') {
        toolMode = 'xml';
        if (hasNativeToolTurns(messages)) {
          messages.splice(0, messages.length, ...nativeToXmlMessages(messages));
        }
        messages[0] = {
          role: 'system',
          content: host.buildSystemPrompt({ extraChunks: extra, toolMode: 'xml' }),
        };
        turn -= 1;
        continue;
      }
      const message = err instanceof Error ? err.message : 'Agent request failed';
      emit({ type: 'error', message });
      return finish('error');
    }

    if (isAborted()) {
      return finish('abort');
    }

    let parsed: ParseResult;
    let nativeIds: string[] = [];
    let nativeInvalid: NativeToolCall[] = [];
    let nativeIncomplete: NativeToolCall | null = null;
    const native = toolCalls.length > 0;

    if (native) {
      const mapped = mapNativeToolCalls(toolCalls);
      parsed = {
        segments: mapped.actions.map((action) => ({ kind: 'action' as const, action })),
        actions: mapped.actions,
        speech: content,
        incomplete: mapped.incomplete != null,
      };
      nativeIds = mapped.ids;
      nativeInvalid = mapped.invalid;
      nativeIncomplete = mapped.incomplete;
    } else if (toolMode === 'native') {
      parsed = {
        segments: content ? [{ kind: 'speech', text: content }] : [],
        actions: [],
        speech: content,
        incomplete: false,
      };
    } else {
      parsed = parseActions(content);
      if (parsed.incomplete && isLengthFinish(finishReason)) {
        if (isAborted()) {
          return finish('abort');
        }
        try {
          const continuationMessages: AgentMessage[] = [
            ...messages,
            { role: 'assistant', content },
            { role: 'user', content: CONTINUE_NUDGE },
          ];
          const continuationRequest = budgeted(continuationMessages);
          notifyPrompt(continuationRequest);
          const continuation = await complete(
            continuationRequest,
            onChunk,
            { content, reasoning },
          );
          content += continuation.content ?? '';
          if (continuation.reasoning) {
            reasoning = `${reasoning ?? ''}${continuation.reasoning}`;
          }
          parsed = parseActions(content);
        } catch (err) {
          if (isAborted()) {
            return finish('abort');
          }
          const message = err instanceof Error ? err.message : 'Agent request failed';
          emit({ type: 'error', message });
          return finish('error');
        }
      }
    }

    emit({
      type: 'assistant_text',
      text: stripFences(content),
      reasoning,
    });

    const toRun = parsed.actions.slice(0, maxActionsPerTurn);
    const extras = parsed.actions.slice(maxActionsPerTurn);
    const results: ActionResult[] = [];
    const nativeResultById = new Map<string, ActionResult>();

    for (let index = 0; index < toRun.length; index += 1) {
      if (isAborted()) {
        return finish('abort');
      }
      const action = toRun[index];
      emit({ type: 'tool_start', toolName: action.name, headers: action.headers });
      const result = host.toolNames.includes(action.name)
        ? await executeAction(host, action)
        : {
            ok: false,
            toolName: action.name,
            message: `unknown_action: ${action.name}`,
          };
      results.push(result);
      emit({ type: 'tool_result', result, action });
      const nativeId = nativeIds[index];
      if (nativeId) nativeResultById.set(nativeId, result);
    }

    for (let extraIndex = 0; extraIndex < extras.length; extraIndex += 1) {
      const extraAction = extras[extraIndex];
      const result: ActionResult = {
        ok: false,
        toolName: extraAction.name,
        message: `too_many_actions: max ${maxActionsPerTurn} per turn`,
      };
      results.push(result);
      emit({ type: 'tool_result', result });
      const nativeId = nativeIds[toRun.length + extraIndex] ?? toolCalls[toRun.length + extraIndex]?.id;
      if (nativeId) nativeResultById.set(nativeId, result);
    }

    // Strict providers reject the next request unless every call id gets a tool reply.
    for (const call of nativeInvalid) {
      const result = formatInvalidNative(call);
      results.push(result);
      emit({ type: 'tool_result', result });
      if (call.id) nativeResultById.set(call.id, result);
    }

    if (parsed.incomplete) {
      const incomplete = nativeIncomplete
        ? formatIncompleteNative(nativeIncomplete, results)
        : formatIncompleteAction(parsed, results);
      results.push(incomplete);
      emit({ type: 'tool_result', result: incomplete });
    }

    if (toRun.length === 0 && extras.length === 0 && !parsed.incomplete) {
      return finish('complete');
    }

    if (native) {
      messages.push({
        role: 'assistant',
        content: content || '',
        tool_calls: toolCalls,
      });
      for (const call of toolCalls) {
        const result = nativeResultById.get(call.id);
        if (result) {
          messages.push({
            role: 'tool',
            tool_call_id: call.id,
            content: result.message,
          });
        }
      }
      if (nativeIncomplete) {
        const last = results[results.length - 1];
        if (last?.toolName === 'incomplete_action') {
          messages.push({
            role: 'tool',
            tool_call_id: nativeIncomplete.id || 'incomplete',
            content: last.message,
          });
        }
      }
    } else {
      messages.push({ role: 'assistant', content });
      messages.push({ role: 'user', content: formatToolResults(results) });
    }
    notifyPrompt();
  }

  return finish('max_turns');
}
